import { useShepherd } from 'vue-shepherd';

interface ShepherdTour {
    addStep: (options: ShepherdStepOptions) => void
    start: () => void
    next: () => void
    back: () => void
    complete: () => void
    cancel: () => void
    on: (event: string, callback: () => void) => void
}

interface ShepherdStepOptions {
    attachTo: {
        element: HTMLElement | string
        on: string
    }
    text: string
    buttons: Array<{
        text: string
        action: () => void
        classes: string
    }>
    scrollTo?: boolean
}

export default class TourService {
    private static tour: ShepherdTour | null = null
    private static buttonBackText: string = 'Back'
    private static buttonCloseText: string = 'Close'
    private static buttonNextText: string = 'Next'
    private static onTourComplete: (() => void) | null = null

    public static initTour(
        buttonBackText = 'Back',
        buttonCloseText= 'Close',
        buttonNextText = 'Next',
        onComplete?: () => void
    ): ShepherdTour {
        this.buttonBackText = buttonBackText;
        this.buttonCloseText = buttonCloseText;
        this.buttonNextText = buttonNextText;
        this.onTourComplete = onComplete || null;

        this.tour = useShepherd({
            useModalOverlay: false,
            defaultStepOptions: {
                scrollTo: true,
                scrollToHandler: (el: HTMLElement) => {
                    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            }
        });

        if (this.tour) {
            this.tour.on('cancel', () => {
                if (this.onTourComplete) {
                    this.onTourComplete();
                }
            });

            this.tour.on('complete', () => {
                if (this.onTourComplete) {
                    this.onTourComplete();
                }
            });
        }

        return <ShepherdTour>this.tour;
    }


    private static isElementVisible(element: HTMLElement): boolean {
        if (window.getComputedStyle(element).display === 'none') {
            return false;
        }

        let parent = element.parentElement;
        while (parent) {
            if (window.getComputedStyle(parent).display === 'none') {
                return false;
            }
            parent = parent.parentElement;
        }

        return true;
    }

    private static getElementBySelector(selector: string | HTMLElement | null): HTMLElement | null {
        if (!selector) {
            return null;
        }

        if (typeof selector === 'string') {
            let element: HTMLElement | null = null;

            if (selector.startsWith('#')) {
                element = document.getElementById(selector.substring(1));
            } else if (selector.startsWith('.')) {
                const elements = document.getElementsByClassName(selector.substring(1));
                if (elements.length > 0) {
                    for (let i = 0; i < elements.length; i++) {
                        const el = elements[i] as HTMLElement;
                        if (this.isElementVisible(el)) {
                            element = el;
                            break;
                        }
                    }

                    if (!element && elements.length > 0) {
                        element = elements[0] as HTMLElement;
                    }
                }
            } else {
                element = document.getElementById(selector);
                if (!element) {
                    const elements = document.getElementsByClassName(selector);
                    if (elements.length > 0) {
                        for (let i = 0; i < elements.length; i++) {
                            const el = elements[i] as HTMLElement;
                            if (this.isElementVisible(el)) {
                                element = el;
                                break;
                            }
                        }

                        if (!element && elements.length > 0) {
                            element = elements[0] as HTMLElement;
                        }
                    }
                }
            }

            return element;
        }

        return selector;
    }


    public static addStep(
        text: string,
        element: HTMLElement | string | null,
        position = 'top',
        isFirst = false,
        isLast = false,
        scrollTo = true,
        onStepComplete?: () => void
    ): void {
        if (!this.tour) {
            this.initTour();
        }

        const targetElement = this.getElementBySelector(element);

        if (!targetElement) {
            console.warn('TourService: Skipping step because element is null or not found');
            return;
        }

        const buttons = [];

        if (!isFirst) {
            buttons.push({
                text: this.buttonBackText,
                action: () => this.tour!.back(),
                classes: 'custom-tour-button-class'
            });
        }

        buttons.push({
            text: this.buttonCloseText,
            action: () => {
                this.tour!.complete();
            },
            classes: 'custom-tour-button-class'
        });

        if (!isLast) {
            buttons.push({
                text: this.buttonNextText,
                action: () => {
                    if (onStepComplete) {
                        onStepComplete();
                    }
                    this.tour!.next();
                },
                classes: 'custom-tour-button-class'
            });
        }

        this.tour!.addStep({
            attachTo: { element: targetElement, on: position },
            text,
            buttons,
            scrollTo
        });
    }

    public static startTour(): void {
        if (!this.tour) {
            this.initTour();
        }
        this.tour!.start();
    }

    public static completeTour(): void {
        if (this.tour) {
            this.tour.complete();
        }
    }

    public static cancelTour(): void {
        if (this.tour) {
            this.tour.cancel();
            if (this.onTourComplete) {
                this.onTourComplete();
            }
        }
    }

    public static getTour(): ShepherdTour | null {
        return this.tour;
    }
}
